/**
 * Ambient "may this user see raw-JSON blocks?" context for the admin shell.
 *
 * Reads the per-user ``show_raw_json`` preference once, at the shell, and
 * hands the answer down. Deep call sites use ``RawJsonBlock`` (or
 * ``useShowRawJson``) and therefore need no query client of their own — the
 * same shape ``HelpProvider`` uses for ``show_inline_help``, and for the same
 * reason: the blocks being gated sit inside small presentational components
 * that are rendered standalone in tests and shouldn't have to grow a data
 * dependency just to be hidden.
 *
 * The context default is ``false``, so every path that misses the provider —
 * anonymous, unresolved auth, a component mounted outside the shell — fails
 * **closed**. That is the whole point of an opt-in preference: a plain
 * operator must never meet a JSON textarea in a form, not even for the frame
 * it takes ``/auth/me`` to resolve.
 */

import { createContext, useContext, type JSX, type ReactNode } from "react";

import { useAuthMe } from "@/api/queries/auth";

const RawJsonContext = createContext<boolean>(false);

export function useShowRawJson(): boolean {
  return useContext(RawJsonContext);
}

export function RawJsonProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const me = useAuthMe();
  return (
    <RawJsonContext.Provider value={me.data?.show_raw_json === true}>
      {children}
    </RawJsonContext.Provider>
  );
}
